import type { Child, Document } from '../api/authoring'

// Addressing, as the model does it.
//
// A placement is addressed by its **path of link ids from the root**,
// never by the component id it points at. The same component linked twice
// is two independently configured placements, and the seeded site already
// contains one — so collapsing the two would not fail in some future edge
// case, it would fail on the content already loaded.

/** A path of link ids from a document's root to one placement. */
export type LinkPath = string[]

/** The URL spelling of a link path. Empty is the document's own root. */
export function encodeLinkPath(path: LinkPath): string {
  return path.join('.')
}

export function decodeLinkPath(encoded: string | null | undefined): LinkPath {
  if (!encoded) return []
  return encoded.split('.').filter(Boolean)
}

export function samePath(left: LinkPath, right: LinkPath): boolean {
  return left.length === right.length && left.every((id, index) => id === right[index])
}

/**
 * Whether one path *is* another, or sits inside it.
 *
 * Compared id by id rather than as two encoded strings, because link ids
 * share prefixes as text and never as paths: `b1` and `b10` are
 * unrelated placements, and the ids this client mints collide the same
 * way on the first duplicate of any type — `content` is a text prefix of
 * `content-2`. Removing a block would take the selection off its sibling.
 */
export function withinPath(inner: LinkPath, outer: LinkPath): boolean {
  return inner.length >= outer.length && outer.every((id, index) => id === inner[index])
}

/**
 * Whether a placement is pinned, and to what.
 *
 * Delivery annotates a *composed* node with `_link.pinned`; the admin
 * reads *authoring* documents, where the same fact is spelled
 * differently: a linked child carries a `revision` when it is pinned and
 * carries none when it resolves to its target's latest. Absent means
 * latest — it is not missing data.
 *
 * An **inline** child has no pin state at all. It has no identity and no
 * separate stream, so it versions and pins with its host; asking whether
 * one is pinned is a category error rather than a `false`.
 */
export type PinState = { kind: 'inline' } | { kind: 'latest' } | { kind: 'pinned'; revision: number }

export function pinStateOf(child: Child): PinState {
  if (!isLinked(child)) return { kind: 'inline' }
  return child.revision === undefined ? { kind: 'latest' } : { kind: 'pinned', revision: child.revision }
}

/**
 * A child is discriminated by the presence of `id`: a linked child names
 * a standalone component, an inline child *is* one, locally, with no
 * identity of its own.
 */
export function isLinked(child: Child): child is Child & { id: string } {
  return typeof child.id === 'string' && child.id.length > 0
}

/** The child a link id names among a node's children. */
export function childByLinkId(children: Child[] | undefined, linkId: string): Child | undefined {
  return children?.find((child) => child.linkId === linkId)
}

/**
 * Walk a link path from a document root to the placement it addresses.
 *
 * The walk descends through **inline** children only, and that is the
 * model's rule rather than this function's convenience: a linked child's
 * content lives in its own stream, so a path cannot continue into it and
 * an intent addressed past one would be a command against the wrong
 * component. Returns undefined rather than throwing — a stale path from
 * a URL is an ordinary thing to be handed.
 */
export function resolveLinkPath(document: Document, path: LinkPath): Child | undefined {
  let children: Child[] | undefined = document.children
  let found: Child | undefined
  for (const linkId of path) {
    found = childByLinkId(children, linkId)
    if (!found) return undefined
    children = found.children
  }
  return found
}

/**
 * Every link id anywhere in a document, inline children included.
 *
 * The model only requires a link id to be unique among its siblings, and
 * this is deliberately stricter — it is what a *path* is written from, so
 * two nodes sharing an id at different depths make `b1.c1` read like a
 * single answer to a question that has two. It costs nothing: minting is
 * the only caller, and it is picking a name nobody has to like.
 */
export function allLinkIds(children: Child[] | undefined): string[] {
  return (children ?? []).flatMap((child) => [child.linkId, ...allLinkIds(child.children)])
}

/**
 * A link id for a new placement, unique against the ones already taken.
 *
 * Derived from the target's id rather than random, because a link id is
 * what an author sees in the tree and what a path is written from, and
 * `contact-intro-2` says more than a uuid does.
 *
 * **One copy, and it used to be five.** Every placement control minted
 * by the same rule and each kept its own function, differing only in the
 * fallback seed — and the duplication had a cost beyond tidiness: two
 * controls that drift produce two link-id conventions in one tenant.
 * It is also load-bearing for rich text, where a body *names* link ids:
 * `docs/plan/inline-bindings.md` §2.3 carries a paste guard precisely
 * because minting is scoped to the document being edited, so a reader of
 * that guard should find one rule to read.
 */
export function mintLinkId(seed: string, taken: Set<string>, fallback = 'block'): string {
  const base = seed.replace(/[^a-zA-Z0-9-]/g, '-').slice(0, 24) || fallback
  if (!taken.has(base)) return base
  for (let suffix = 2; ; suffix += 1) {
    const candidate = `${base}-${suffix}`
    if (!taken.has(candidate)) return candidate
  }
}
